import EmojiEmotionsOutlinedIcon from '@mui/icons-material/EmojiEmotionsOutlined';
import { Box, Button, IconButton, Popover, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { MouseEvent, useState } from 'react';
import { defineMessage, useIntl } from 'react-intl';
import { getEmojiPickerData } from './emoji-picker-locale';
import { EmojiPickerPosition, emojiPickerPlacementByPosition } from './emoji-picker-position';

const addEmojiMessage = defineMessage({
  id: 'fragments.form.addEmoji',
  defaultMessage: 'Agregar emoji',
});

const emojiSearchPlaceholderMessage = defineMessage({
  id: 'fragments.form.emojiSearchPlaceholder',
  defaultMessage: 'Buscar',
});

const emojiPickerOkMessage = defineMessage({
  id: 'fragments.form.emojiPickerOk',
  defaultMessage: 'Aceptar',
});

export type EmojiPickerButtonProps = {
  /** Called with the selected emoji character when the user picks one. */
  onSelect: (emoji: string) => void;
  /** Where the picker opens relative to the input. Defaults to `bottom`. */
  position?: EmojiPickerPosition;
};

/** Finds the text field input associated with an adornment emoji button. */
function findRelatedInput(anchor: HTMLElement | null) {
  return anchor
    ?.closest('.MuiInputBase-root')
    ?.querySelector<HTMLInputElement | HTMLTextAreaElement>('textarea, input');
}

/**
 * Icon button that opens an emoji picker popover and reports the selected emoji via onSelect.
 */
export function EmojiPickerButton({ onSelect, position = 'bottom' }: EmojiPickerButtonProps) {
  const { $t, locale } = useIntl();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const popoverOpen = Boolean(anchorEl);
  const { anchorOrigin, transformOrigin } = emojiPickerPlacementByPosition[position];

  function handleOpenPicker(event: MouseEvent<HTMLElement>) {
    // Anchor to the whole field so the picker sits relative to the input, not the icon.
    const fieldRoot = event.currentTarget.closest('.MuiInputBase-root') as HTMLElement | null;
    setAnchorEl(fieldRoot ?? event.currentTarget);
  }

  function handleClosePicker() {
    setAnchorEl(null);
  }

  /** Closes the picker and restores focus to the field that opened it. */
  function handleConfirm() {
    const input = findRelatedInput(anchorEl);
    setAnchorEl(null);
    // Focus after MUI tears down the popover so it does not steal focus back.
    queueMicrotask(() => input?.focus());
  }

  function handleSelectEmoji(emojiData: EmojiClickData) {
    onSelect(emojiData.emoji);
  }

  return (
    <>
      <Tooltip title={$t(addEmojiMessage)}>
        <IconButton size="small" onClick={handleOpenPicker}>
          <EmojiEmotionsOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Popover
        anchorEl={anchorEl}
        open={popoverOpen}
        onClose={handleClosePicker}
        disableRestoreFocus
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
      >
        <Box
          sx={{
            '& .epr-emoji-category-label': {
              typography: 'subtitle2',
              fontWeight: 'normal',
              height: '20px',
              fontSize: '12px',
            },
          }}
        >
          <EmojiPicker
            onEmojiClick={handleSelectEmoji}
            theme={theme.palette.mode === 'dark' ? Theme.DARK : Theme.LIGHT}
            emojiData={getEmojiPickerData(locale)}
            searchPlaceholder={$t(emojiSearchPlaceholderMessage)}
            skinTonesDisabled
            previewConfig={{
              showPreview: false,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: 1,
              py: 0.75,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <Button size="small" variant="contained" onClick={handleConfirm}>
              {$t(emojiPickerOkMessage)}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}
